import type { Except } from '@festina-life/core'
import { Dialog, DialogBackdrop, DialogTitle } from '@headlessui/react'
import cx from 'classnames'
import { type HTMLAttributes, type ReactNode, Suspense } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { ButtonContainer } from '../../actions'
import { IconButton } from '../../actions/iconButton/IconButton'
import { CircularSpinner } from '../../feedback/circularSpinner/CircularSpinner'
import { ErrorFallback } from '../../feedback/errorFallback/ErrorFallback'
import { UnsavedChangesProvider } from '../../forms/context/UnsavedChangesProvider'
import { useTranslate } from '../../hooks/useTranslate'
import { zIndexStyles } from '../../libs/zIndexStyles'
import { TableCellContext } from '../../listsAndTables/table/tableCellContext'
import type { ClassNameType } from '../../types/component'
import { Stack } from '../stack/Stack'
import { typographyStyles } from '../typography/styles'
import { backdropStyles } from './backdropStyles'
import { useModalContext } from './context/modalContext'
import { ModalDraggableDialogPanel } from './ModalDraggableDialogPanel'
import type { ModalProps } from './types'

export const Modal = ({
  isOpen,
  shouldUnmountChildrenOnClose = true,
  className,
  onClose,
  isGlobal = false,
  ...restProps
}: ModalProps) => (
  <TableCellContext
    // This ensures that the modal is not considered part of a table cell context
    value={false}
  >
    <UnsavedChangesProvider
      onClose={onClose}
      isOpen={isOpen}
      isDirty={false}
      isSubmitting={false}
    >
      {({ onClose: onCloseOverwrite }) => (
        <Dialog
          open={isOpen}
          onClose={onCloseOverwrite}
          transition
          className={cx(
            'opacity-100 data-closed:opacity-0 transition-opacity duration-300 flex items-center justify-center h-dvh w-dvw',
            'outline-0', // ensure focus trapping doesn't produce an unwanted outline
            'fixed inset-0',
            zIndexStyles(isGlobal ? 'globalModals' : 'modals'),
            className,
          )}
          unmount={shouldUnmountChildrenOnClose}
        >
          <DialogBackdrop className={backdropStyles} />
          <ModalDraggableDialogPanel
            isOpen={isOpen}
            onClose={onClose}
            {...restProps}
          />
        </Dialog>
      )}
    </UnsavedChangesProvider>
  </TableCellContext>
)

const Body = ({
  children,
  innerClassName,
  outerClassName,
  ...htmlProps
}: Except<HTMLAttributes<HTMLElement>, 'className'> & {
  innerClassName?: string
  outerClassName?: string
}) => (
  <Stack
    className={cx(
      'overflow-y-auto min-h-0 flex-col grow px-3 group/outerBody',
      outerClassName,
    )}
    gap="none"
    {...htmlProps}
  >
    <Stack
      className={cx(
        'flex-col py-1.5',
        'group-last/outerBody:pb-3', // dd some space at the bottom of the modal body if it's the last child
        innerClassName,
      )}
    >
      <Suspense fallback={<CircularSpinner />}>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          {children}
        </ErrorBoundary>
      </Suspense>
    </Stack>
    <div
      /* this needs to be here for bottom padding on stack sibling before to kick in */
      className="h-0"
    />
  </Stack>
)
Modal.Body = Body

type TitleProps = {
  title: ReactNode
  extraAction?: ReactNode
}

const Title = ({
  title,
  className,
  extraAction,
}: ClassNameType & TitleProps) => {
  const translate = useTranslate()
  const { onClose } = useModalContext()

  return (
    <DialogTitle
      as={typeof title === 'string' ? 'h2' : 'div'}
      id="draggable-handle" // used by react-draggable to identify the drag handle
      className={`${cx(
        'flex items-start shrink-0',
        'in-[.react-draggable]:cursor-move', // update cursor icon when it can be dragged
        'in-[.react-draggable]:select-none', // prevent text selection while dragging (otherwise it select random parts of the title while dragging)
        'px-3 pt-3 pb-1.5',
        typographyStyles({ size: 'xl', weight: 'semibold' }),
        className,
      )}`}
    >
      {title}
      <div className="flex items-center ml-auto -my-1 -mr-1">
        {extraAction}
        <IconButton
          aria-label={translate('close')}
          onClick={onClose}
          tip={translate('close')}
          iconName="XMark"
          iconSize="md"
        />
      </div>
    </DialogTitle>
  )
}
Modal.Title = Title

const Footer = ({
  children,
  className,
}: ClassNameType & HTMLAttributes<HTMLElement>) => (
  <ButtonContainer
    className={cx('sticky shrink-0 justify-end px-3 pt-1.5 pb-3', className)}
  >
    {children}
  </ButtonContainer>
)
Modal.Footer = Footer
